import {
  ArrowLeftOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  Modal,
  Skeleton,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import EmailEditor from "../../../shared/emailEditor/EmailEditor";
import useUnsavedChangesGuard from "../../../shared/emailEditor/useUnsavedChangesGuard";
import {
  fetchEmailSignature,
  fetchRenewalEmailPreview,
  fetchRenewalForm,
  resolveRenewalEmailDelivery,
  sendRenewalEmail,
} from "../../../store/renewalsSearch/renewalsSearchThunks";
import ApplicationHeaderBar from "../../applications/shared/ApplicationHeaderBar";
import EditSignatureModal from "./EditSignatureModal";
import EmailDeliveryReview from "./EmailDeliveryReview";
import RenewalEmailAttachments from "./RenewalEmailAttachments";
import SentEmailView from "./SentEmailView";
import { renewalEmailWarnings } from "./renewalEmailWarnings";

const { Text } = Typography;

const EMAIL_MAX_WIDTH = 800;

// The email that already went out for this renewal, or null while nothing has been sent
const sentEmailFrom = (renewal) =>
  ["sent", "completed"].includes(renewal.status)
    ? {
        subject: renewal.sent_subject,
        body: renewal.sent_body,
        sent_at: renewal.sent_at,
        reviewed_at: renewal.email_delivery_reviewed_at,
      }
    : null;

const SendRenewalEmailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { renewalId } = useParams();

  const isSendingEmail = useSelector(
    (state) => state.renewalsSearch.viewRenewal.isSendingEmail,
  );
  const isAdmin = useSelector((state) => state.user.admin);

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [renewalData, setRenewalData] = useState(null);
  // { subject, from, to, body, application_url, application_number } from the backend
  const [compose, setCompose] = useState(null);
  const [subject, setSubject] = useState("");
  const [dirty, setDirty] = useState(false);
  // Bumped to reload the editor with fresh content
  const [editorVersion, setEditorVersion] = useState(0);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [resolvingDelivery, setResolvingDelivery] = useState(false);
  const [attachmentFileList, setAttachmentFileList] = useState([]);
  const [includePreviousApplication, setIncludePreviousApplication] =
    useState(true);
  const [sentEmail, setSentEmail] = useState(null);
  const editorRef = useRef(null);

  const loadCompose = async (id) => {
    const preview = await dispatch(fetchRenewalEmailPreview({ id })).unwrap();
    setCompose(preview);
    setSubject(preview.subject);
    setEditorVersion((version) => version + 1);
    setDirty(false);
  };

  const loadPage = async (id) => {
    setLoading(true);
    setFailed(false);
    setSentEmail(null);
    setCompose(null);
    setAttachmentFileList([]);
    setDirty(false);

    try {
      const data = await dispatch(fetchRenewalForm({ id })).unwrap();
      setRenewalData(data);

      if (data.email_delivery_state) return;

      const sent = sentEmailFrom(data);
      if (sent) {
        setSentEmail(sent);
        return;
      }

      if (!data.coverage_date) {
        message.error("Please set a coverage date before sending an email.");
        throw new Error("Missing coverage date");
      }

      const settings = await dispatch(fetchEmailSignature()).unwrap();
      setSignatureHtml(settings.email_signature ?? "");
      await loadCompose(id);
    } catch (e) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (renewalId) {
      loadPage(renewalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewalId]);

  const deliveryNeedsReview = Boolean(renewalData?.email_delivery_state);
  const showSentEmail = !deliveryNeedsReview && sentEmail !== null;
  const composing = !deliveryNeedsReview && sentEmail === null;

  useUnsavedChangesGuard(dirty && composing);

  const currentBody = () => editorRef.current?.getHTML() ?? "";

  const deliver = async (attachment) => {
    try {
      const result = await dispatch(
        sendRenewalEmail({
          id: renewalId,
          attachment,
          subject: subject.trim(),
          body: currentBody(),
          includePreviousApplication,
        }),
      ).unwrap();

      message.success("Email sent successfully.");
      setSentEmail(sentEmailFrom(result));
      setRenewalData((data) => ({ ...data, ...result }));
      setDirty(false);
      setAttachmentFileList([]);
    } catch (error) {
      if (error?.status !== 409) return;

      // Someone else sent it, or an earlier attempt awaits review: show that instead of the draft
      try {
        const latest = await dispatch(fetchRenewalForm({ id: renewalId })).unwrap();
        const sent = sentEmailFrom(latest);
        if (latest.email_delivery_state || sent) {
          setRenewalData(latest);
          setSentEmail(sent);
          setDirty(false);
          setAttachmentFileList([]);
        }
      } catch (_) {
        // Keep the draft when the latest delivery state cannot be loaded.
      }
    }
  };

  const handleSend = () => {
    if (!renewalId || !compose) return;

    if (!subject.trim()) {
      message.error("Please enter a subject line.");
      return;
    }
    if (editorRef.current?.isEmpty()) {
      message.error("The email body can't be empty.");
      return;
    }

    const attachment = attachmentFileList[0]?.originFileObj || attachmentFileList[0];
    const warnings = renewalEmailWarnings({
      subject: subject.trim(),
      body: currentBody(),
      compose,
      includePreviousApplication,
      hasCertificate: Boolean(attachment),
    });
    if (warnings.length === 0) {
      deliver(attachment);
      return;
    }

    Modal.confirm({
      title: "Review this email before sending",
      content: (
        <ul>
          {warnings.map((warning) => <li key={warning}>{warning}</li>)}
        </ul>
      ),
      okText: "Send anyway",
      okButtonProps: { danger: true },
      cancelText: "Go back",
      onOk: () => deliver(attachment),
    });
  };

  const handleSignatureSaved = async (html) => {
    setSignatureHtml(html);
    setSignatureModalOpen(false);

    if (dirty) {
      message.info(
        "Signature saved. Use Insert signature to place the new one in this email.",
      );
      return;
    }

    // Nothing edited yet: start over from the template with the new signature in place
    setLoading(true);
    try {
      await loadCompose(renewalId);
    } catch (_) {
      // The current draft stays; the API error notifications say what went wrong
    } finally {
      setLoading(false);
    }
  };

  const handleResolveDelivery = (resolution) => {
    Modal.confirm({
      title:
        resolution === "sent" ? "Confirm this email was sent?" : "Allow another send?",
      content:
        "Check the outbound mail record before deciding. An incorrect choice could hide an undelivered email or send a duplicate.",
      okText: resolution === "sent" ? "Mark as sent" : "Allow retry",
      onOk: async () => {
        setResolvingDelivery(true);
        try {
          await dispatch(
            resolveRenewalEmailDelivery({ id: renewalId, resolution }),
          ).unwrap();
          await loadPage(renewalId);
        } finally {
          setResolvingDelivery(false);
        }
      },
    });
  };

  const headerActions = [
    <Button key="back" size="small" onClick={() => navigate(-1)}>
      <ArrowLeftOutlined /> Go Back To Renewal
    </Button>,
    composing && (
      <Button
        key="edit-signature"
        size="small"
        onClick={() => setSignatureModalOpen(true)}
        icon={<EditOutlined />}
      >
        Edit Signature
      </Button>
    ),
    composing && (
      <Button
        key="send"
        size="small"
        type="primary"
        icon={<SendOutlined />}
        loading={isSendingEmail}
        disabled={isSendingEmail}
        onClick={handleSend}
      >
        Send Email
      </Button>
    ),
  ].filter(Boolean);

  if (failed) {
    return (
      <Alert
        type="error"
        showIcon
        message="Error: Something went wrong while loading this page."
        className="default-margin"
      />
    );
  }

  return (
    <Card className="body-card-content-no-min-height">
      <Skeleton active loading={loading}>
        <ApplicationHeaderBar
          data={renewalData}
          actions={headerActions}
          isRenewal
        />
        <Divider style={{ marginTop: 8, marginBottom: 16 }} dashed />

        {deliveryNeedsReview && (
          <EmailDeliveryReview
            renewal={renewalData}
            isAdmin={isAdmin}
            resolving={resolvingDelivery}
            onResolve={handleResolveDelivery}
          />
        )}

        {showSentEmail && <SentEmailView sentEmail={sentEmail} />}

        {composing && (
          <div style={{ maxWidth: EMAIL_MAX_WIDTH }}>
            <div style={{ marginBottom: 12 }}>
              <div>
                <strong>From:</strong> {compose?.from ?? "—"}
              </div>
              <div>
                <strong>To:</strong> {compose?.to ?? renewalData?.email ?? "—"}
                {/* Only sent outside production, where mail is redirected */}
                {compose?.delivers_to && (
                  <Tooltip
                    title={`Outside production, this email will really go to ${compose.delivers_to}`}
                  >
                    <InfoCircleOutlined
                      style={{
                        marginLeft: 6,
                        color: "rgba(0,0,0,0.3)",
                        fontSize: 13,
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Subject</Text>
              <Input
                style={{ marginTop: 4 }}
                value={subject}
                maxLength={255}
                placeholder="Subject line"
                onChange={(event) => {
                  setSubject(event.target.value);
                  setDirty(true);
                }}
              />
            </div>

            {compose && (
              <EmailEditor
                key={editorVersion}
                ref={editorRef}
                defaultValue={compose.body}
                signature={{ html: signatureHtml }}
                onChange={() => setDirty(true)}
                placeholder="Write your email…"
                minHeight={420}
              />
            )}

            <RenewalEmailAttachments
              includePreviousApplication={includePreviousApplication}
              onIncludePreviousApplicationChange={(include) => {
                setIncludePreviousApplication(include);
                if (!include) setDirty(true);
              }}
              fileList={attachmentFileList}
              onFileListChange={(files) => {
                setAttachmentFileList(files);
                setDirty(true);
              }}
            />
          </div>
        )}

        <EditSignatureModal
          open={signatureModalOpen}
          onClose={() => setSignatureModalOpen(false)}
          onSaved={handleSignatureSaved}
        />
      </Skeleton>
    </Card>
  );
};

const KeyedSendRenewalEmailPage = () => {
  const { renewalId } = useParams();
  return <SendRenewalEmailPage key={renewalId} />;
};

export default KeyedSendRenewalEmailPage;
